import { ReactElement, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bin,
  ChannelIcon,
  EllipsisMenu,
  IOption,
  FormatInformation,
  Info,
  PriceBandIcon,
  PricingIcon,
  Prompt,
  Tooltip,
  Typography,
  SnackbarStatus,
  useSnackbar,
} from '@weareopenr/component-library';
import { IMenuItem, IOptionGroup } from 'src/types/menu.types';
import styles from './ModifierCategory.module.scss';
import { deleteModifierGroup } from 'src/api/deleteModifierGroup/deleteModifierGroup';
import { reorderModifierGroup } from 'src/api/reorderModifierGroup/reorderModifierGroup';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import Modifier from '../Modifier';
import { ItemType } from 'src/constants';

interface ModifierCategoryProps {
  index: number;
  optionGroup: IOptionGroup;
  currentItem: IMenuItem;
  openOptionGroupModal: (groupToEdit?: IOptionGroup) => void;
  openOptionGroupPricingModal: (groupToEdit?: IOptionGroup) => void;
  openOptionGroupExternalSystemsModal?: (groupToEdit: IOptionGroup) => void;
}

const selectionCriteria = (min?: number, max?: number): string => {
  if (min && max) return `Min of ${min} and Max of ${max}`;
  else if (min) return `Min of ${min}`;
  else if (max) return `Max of ${max}`;
  else return 'None';
};

export const ModifierCategory = ({
  index,
  optionGroup,
  currentItem,
  openOptionGroupModal,
  openOptionGroupPricingModal,
  openOptionGroupExternalSystemsModal,
}: ModifierCategoryProps): ReactElement => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const isBundle = currentItem?.type === ItemType.BUNDLE;

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const externalSystems =
    optionGroup.external_systems.map((system) => system.name).join(', ') || 'No channels selected';

  const refetchItem = async () => {
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, currentItem.itemId] });
  };

  const handleDeleteOptionGroup = async (): Promise<void> => {
    try {
      const remainingOptionGroups = currentItem.optionGroup.filter(
        (group) => group.optionGroupId !== optionGroup.optionGroupId,
      );
      queryClient.setQueriesData<IMenuItem>(['item', currentItem.itemId], {
        ...currentItem,
        optionGroup: remainingOptionGroups,
      });
      await deleteModifierGroup(currentItem.itemId, optionGroup.optionGroupId);
      refetchItem();
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted ${isBundle ? 'bundle category' : 'modifier group'} ${optionGroup.name}.`,
      });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst deleting ${isBundle ? 'bundle category' : 'modifier group'} ${optionGroup.name}.`,
      });
    } finally {
      refetchItem();
    }
  };

  const moveOptionGroup = async (allOptionGroups: IOptionGroup[], from: number, to: number) => {
    const newGroups = [...allOptionGroups];
    const itemToMove = newGroups[from];
    newGroups[from] = newGroups[to];
    newGroups[to] = itemToMove;

    try {
      queryClient.setQueriesData<IMenuItem>(['item', currentItem.itemId], {
        ...currentItem,
        optionGroup: newGroups,
      });
      await reorderModifierGroup(currentItem.itemId, {
        modifier_group_ids: newGroups.map((option) => option.optionGroupId),
      });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst moving ${isBundle ? 'bundle category' : 'modifier group'}.`,
      });
    } finally {
      refetchItem();
    }
  };

  const options: IOption[] = [
    {
      id: 'edit-details',
      name: 'Edit Category',
      onOptionClick: () => openOptionGroupModal(optionGroup),
      icon: PriceBandIcon,
    },
    ...(!isBundle
      ? [
          {
            id: 'edit-external-systems',
            name: 'Edit Publish Channels',
            onOptionClick: () => openOptionGroupExternalSystemsModal(optionGroup),
            icon: ChannelIcon,
          },
        ]
      : []),
    {
      id: 'edit-prices',
      name: 'Edit Prices',
      onOptionClick: () => openOptionGroupPricingModal(optionGroup),
      icon: PricingIcon,
    },
    {
      id: 'up',
      name: 'Move up',
      icon: ArrowUpIcon,
      onOptionClick: () => moveOptionGroup(currentItem.optionGroup, index, index - 1),
      isDisabled: index === 0,
      dataTestId: 'move-up-button',
    },
    {
      id: 'down',
      name: 'Move down',
      icon: ArrowDownIcon,
      onOptionClick: () => moveOptionGroup(currentItem.optionGroup, index, index + 1),
      isDisabled: currentItem.optionGroup.length === index + 1,
      dataTestId: 'move-down-button',
    },
  ];

  if (
    currentItem.type !== ItemType.BUNDLE ||
    (currentItem.type === ItemType.BUNDLE && currentItem.optionGroup.length > 1)
  ) {
    options.push(
      { id: 'delete-divider', isDivider: true },
      {
        id: 'delete',
        name: 'Delete',
        icon: Bin,
        onOptionClick: () => setShowDeleteModal(true),
        hoverColour: 'tomato',
        dataTestId: 'delete-button',
      },
    );
  }

  return (
    <>
      <Prompt
        promptHeading={`Delete ${isBundle ? 'bundle' : 'modifier'} category?`}
        closePrompt={() => setShowDeleteModal(false)}
        open={showDeleteModal}
        buttonHandler={handleDeleteOptionGroup}
        buttonText="Delete"
        backButtonText="Cancel"
        backButton={true}
        size="small"
      >
        This action cannot be undone.
      </Prompt>
      <div className={styles['container']} key={optionGroup.optionGroupId}>
        <div className={styles['information-container']}>
          <div className={styles['info-column']}>
            <FormatInformation heading="Name">{optionGroup.name}</FormatInformation>
            <FormatInformation heading="Consumer Name">{optionGroup.consumer_name || '-'} </FormatInformation>
            {!isBundle && <FormatInformation heading="Description">{optionGroup.description}</FormatInformation>}
          </div>
          <div className={styles['info-column']}>
            <FormatInformation heading={'Range of choices'}>
              {selectionCriteria(optionGroup.min, optionGroup.max)}
            </FormatInformation>
            {!isBundle && (
              <FormatInformation
                heading="Non-repeatable"
                tooltip={
                  <Tooltip text="Selecting 'Non-repeatable' limits customers to choosing one option per modifier group when ordering. If unselected, customers can choose multiple of the same modifier within a group">
                    <Info />
                  </Tooltip>
                }
              >
                {optionGroup.is_repeatable ? 'No' : 'Yes'}
              </FormatInformation>
            )}
            {!isBundle && <FormatInformation heading="Publishing channels">{externalSystems}</FormatInformation>}
          </div>
          <EllipsisMenu data-testid="menu-options-button" options={options} variant="ghost" />
        </div>

        <div>
          <Typography size="medium" weight="bold">
            {isBundle ? 'Products' : 'Modifiers'}
          </Typography>
          <div className={styles['modifiers-container']}>
            {optionGroup.items.map(({ optionItemId, name, valueWithTax, posId, inheritPrice }) => (
              <Modifier
                key={optionItemId}
                name={name}
                price={valueWithTax}
                inheritPrice={inheritPrice}
                posId={posId}
                link={`/items/${optionItemId}`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModifierCategory;
